import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import Presentation from './Presentation';
import { useLoader } from '../../../../HOC/LoaderProvider';
import { useBooth } from '../../../../hooks/useBooth';
import { Booth } from '../../../../interfaces/interfaces';
import { fetchAndSetAdminBooth } from '../../../../reduxToolkit/slices/adminSlice';
import { useDispatch, useSelector } from '../../../../reduxToolkit/typedRedux';

function BoothPresentations({
  booth,
  showTitle = false,
  withoutChatrooms = false,
  isInline = false,
  onRefresh,
}: {
  booth?: Booth;
  showTitle?: boolean;
  withoutChatrooms?: boolean;
  isInline?: boolean;
  onRefresh?: () => void;
}) {
  const [maxExceded, setMaxExceded] = useState('');
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const event = useSelector((state) => state.common.event);
  const { createPresentationRoom } = useBooth();
  const { startLoader, stopLoader } = useLoader();

  const [isCreating, setIsCreating] = useState(false);

  // find a better way to show plus sign when some of the presentations is deleted
  useEffect(() => {
    if (maxExceded !== '') {
      setMaxExceded('');
    }
  }, [booth?.presentationrooms]);

  function fetchBooth() {
    if (!booth) return;
    if (!isInline) dispatch(fetchAndSetAdminBooth({ boothid: booth._id }));
    else onRefresh?.();
  }

  const _addNewPresentation = async () => {
    if (!booth) return;
    setIsCreating(true);
    !isInline && startLoader();
    await createPresentationRoom(booth._id, fetchBooth, (err: any) => setMaxExceded(err?.response?.data?.translation?.['de-DE'] || 'Error.'));
    setIsCreating(false);
    !isInline && stopLoader();
  };

  if (!booth) return null;

  if (booth.maxPresentations === 0 && !booth.hasUnlimitedRooms) return null;

  return (
    <>
      {showTitle && <h2 className="self-start text-xl text-black">{event?.template.individualNames.presentationrooms || t('account:presentations')}</h2>}
      <div className="grid w-full max-w-full grid-cols-1 gap-4 place-items-center md:place-content-around md:grid-cols-2">
        {booth?.presentationrooms &&
          booth.presentationrooms.map((presentation) => (
            <Presentation
              key={presentation.id}
              id={presentation._id}
              withoutChatrooms={withoutChatrooms}
              booth={booth}
              onRefresh={onRefresh}
              isInline={isInline}
            />
          ))}
      </div>
      <div className="flex justify-center max-w-full pb-4 mt-4">
        {maxExceded === '' && (
          <div>
            {booth.hasUnlimitedRooms || (booth.presentationrooms?.length ?? 0) < booth.maxPresentations ? (
              <div className="flex flex-row justify-center w-full space-x-4 text-center">
                {!isCreating ? (
                  <div
                    onClick={() => _addNewPresentation()}
                    data-cy="create-new-presentation"
                    className="flex items-center justify-center w-10 h-10 text-white rounded-full opacity-50 cursor-pointer bg-slate-700 hover:opacity-75">
                    <FontAwesomeIcon icon={faPlus} />
                  </div>
                ) : (
                  <CircularProgress />
                )}
                {!booth.hasUnlimitedRooms && (
                  <div className="mt-2">
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      {t('booth:presentations--limit', { limit: booth.maxPresentations })}
                    </Typography>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  {t('booth:presentations-limit-exceeded')}
                </Typography>
              </div>
            )}
          </div>
        )}
        {maxExceded !== '' && <h1 className="text-black">{maxExceded}</h1>}
      </div>
    </>
  );
}

export default BoothPresentations;
